import { Ref } from 'vue';
import { extractThemeBlock } from '../utils/themeExtraction.utils';
import { injectStylesheet, generateCSSClasses } from '../utils/stylesheetInjection.utils';
import { useInjectThemeEntity } from './useInjectThemeEntity';
import { PUI } from '@pleased-npm/fe-customisation';

interface FontConfig {
  [PUI.THEME_FIELD_KEYS.FONT]: string;
  [PUI.THEME_FIELD_KEYS.STYLE]: string;
  [PUI.THEME_FIELD_KEYS.SIZE]: number;
}

interface ThemeFonts {
  h1?: FontConfig;
  h2?: FontConfig;
  h3?: FontConfig;
  h4?: FontConfig;
  mainText?: FontConfig;
  subtext?: FontConfig;
}

const STYLE_ID = 'theme-fonts-stylesheet' as const;

// Section mapping for font extraction
const FONT_SECTION_MAPPING: Record<string, keyof ThemeFonts> = {
  [PUI.THEME_SECTION_KEYS.HEADING_1]: 'h1',
  [PUI.THEME_SECTION_KEYS.HEADING_2]: 'h2',
  [PUI.THEME_SECTION_KEYS.HEADING_3]: 'h3',
  [PUI.THEME_SECTION_KEYS.HEADING_4]: 'h4',
  [PUI.THEME_SECTION_KEYS.MAIN_TEXT]: 'mainText',
  [PUI.THEME_SECTION_KEYS.SUBTEXT]: 'subtext',
} as const;

// Class name mapping for CSS generation
const FONT_CLASS_MAPPING: Record<keyof ThemeFonts, string> = {
  h1: 'theme-font-h1',
  h2: 'theme-font-h2',
  h3: 'theme-font-h3',
  h4: 'theme-font-h4',
  mainText: 'theme-font-main',
  subtext: 'theme-font-subtext',
} as const;

// Font style mapping (bold -> font-weight: 700)
const FONT_STYLE_MAPPING = {
  normal: { fontWeight: '400', fontStyle: 'normal' },
  bold: { fontWeight: '700', fontStyle: 'normal' },
  italic: { fontWeight: '400', fontStyle: 'italic' },
} as const;

/**
 * Extracts font configurations from the theme document
 */
function extractFontsFromTheme(theme: any): ThemeFonts {
  return extractThemeBlock<ThemeFonts>(
    theme,
    PUI.THEME_BLOCK_KEYS.TEXT_STYLES,
    FONT_SECTION_MAPPING,
    // Validator: only include if we have essential properties
    (config) => config[PUI.THEME_FIELD_KEYS.SIZE] !== undefined
  );
}

/**
 * Generates CSS for a single font configuration
 */
function generateFontCSS(font: FontConfig): string {
  const fontFamily = font[PUI.THEME_FIELD_KEYS.FONT] || 'Roboto';
  const fontSize = `${font[PUI.THEME_FIELD_KEYS.SIZE]}px`;
  const styleValue = font[PUI.THEME_FIELD_KEYS.STYLE] || 'normal';

  // Map style value to font-weight and font-style
  const styleMapping = FONT_STYLE_MAPPING[styleValue as keyof typeof FONT_STYLE_MAPPING] || FONT_STYLE_MAPPING.normal;
  const fontWeight = styleMapping.fontWeight;
  const fontStyle = styleMapping.fontStyle;

  return `  font-family: '${fontFamily}', sans-serif;
  font-size: ${fontSize};
  font-weight: ${fontWeight};
  font-style: ${fontStyle};`;
}

/**
 * Applies font configurations by injecting CSS classes
 */
function applyFonts(fonts: ThemeFonts): void {
  // Generate and inject CSS stylesheet
  const css = generateCSSClasses(FONT_CLASS_MAPPING, fonts, (font: FontConfig) => generateFontCSS(font));

  injectStylesheet(STYLE_ID, css);
}

/**
 * Composable that injects theme fonts as CSS classes into the document
 *
 * This composable watches the active theme and dynamically injects
 * CSS classes (theme-font-h1, theme-font-h2, etc.) with font styles
 * from the theme's text-styles block.
 *
 * Usage in component:
 * ```ts
 * const { isApplied } = useInjectThemeFonts();
 *
 * // In template:
 * <h1 class="theme-font-h1">My Heading</h1>
 * <p class="theme-font-main">My text</p>
 * ```
 *
 * Available classes:
 * - .theme-font-h1
 * - .theme-font-h2
 * - .theme-font-h3
 * - .theme-font-h4
 * - .theme-font-main
 * - .theme-font-subtext
 *
 * Each class applies:
 * - font-family (from theme FONT field)
 * - font-size (from theme SIZE field)
 * - font-weight and font-style (from theme STYLE field: 'normal' | 'bold' | 'italic')
 *
 * @returns {Object} - Object containing isApplied ref indicating if fonts are loaded
 */
export function useInjectThemeFonts(): { isApplied: Ref<boolean> } {
  return useInjectThemeEntity<ThemeFonts>({
    styleId: STYLE_ID,
    extractor: extractFontsFromTheme,
    applier: applyFonts,
    validator: (fonts) => Object.keys(fonts).length > 0,
  });
}
